/// <amd-module name="Environments/Pipelines/Run/EnvironmentsRunTabService" />
import React = require("react");

import { IRunViewContributedTabProps } from 'Build/Run/RunViewContributedTab';

import { IEnvironmentRunsResponse, IEnvironmentRunObject } from "Environments/Client/EnvironmentContracts";
import { EnvironmentsTabComponent } from "Environments/Pipelines/Run/EnvironmentsRunTabContent";
import * as Resources from "Environments/Pipelines/Run/Resources";

import { ObservableArray, IReadonlyObservableArray } from "VSS/Core/Observable";
import { ITabService } from "VSS/Features/PlatformUI/ContributedTab";
import { Services, VssService, IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { Observer } from "VSSUI/Observer";
import { IVssContributedTab } from "VSSUI/Tabs.Types";

class EnvironmentsTabProviderService extends VssService implements ITabService {
    public getTabs(tabId: string, contextualData?: IRunViewContributedTabProps): IVssContributedTab[] | IReadonlyObservableArray<IVssContributedTab> {

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

        if (contextualData && contextualData.notifyUpdated) {
            this._contextualData = contextualData;

            // try to get the data synchronously first
            // if syncData is not undefined, it means that the data-provider has been loaded
            const syncData: IEnvironmentRunsResponse | undefined  = contributionService.getData<IEnvironmentRunsResponse>(this._dataProviderId);
            if (syncData) {
                if (syncData.environmentRunObjects && syncData.environmentRunObjects.length > 0) {
                    this._environmentsRunData.value = syncData.environmentRunObjects;
                    this._tabs.push(this._tab);
                }
            }
            else {
                // data-provider has not been loaded
                // try to get data asynchronously
                this._updateEnvironmentsTab();
            }

            this._contextualData.notifyUpdated.subscribe(this._updateEnvironmentsTab);
        }

        return this._tabs;
    }

    public _serviceEnd(pageContext: IVssPageContext): void {
        this._contextualData && this._contextualData.notifyUpdated && this._contextualData.notifyUpdated.unsubscribe(this._updateEnvironmentsTab);
        super._serviceEnd(pageContext);
    }

    /**
     * This function has 2 purposes:
     * 1. It checks if the environments tab should be shown.
     * 2. It updates the environments tab UI with the latest status whenever there is a notifyUpdated event fired
     */
    private _updateEnvironmentsTab = async (): Promise<void> => {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const data = await contributionService.getDataAsync<IEnvironmentRunsResponse>(this._dataProviderId, undefined, true);

        if (data && data.environmentRunObjects && data.environmentRunObjects.length > 0) {
            this._environmentsRunData.value = data.environmentRunObjects;

            if (this._tabs.length === 0) {    
                this._tabs.push(this._tab);
            }
        }
    }

    private _renderEnvironments = (): JSX.Element => {
        return (
            <Observer data={this._environmentsRunData}>
                {(props: { data: IEnvironmentRunObject[] }) => (
                    <EnvironmentsTabComponent environmentsRuns={props.data} />
                )}
            </Observer>
        );
    }

    private readonly _tab: IVssContributedTab = {
        id: "environments",
        name: Resources.EnvironmentsTabText,
        order: 3,
        render: this._renderEnvironments
    };

    private _contextualData: IRunViewContributedTabProps;
    private _tabs = new ObservableArray<IVssContributedTab>();
    private _environmentsRunData = new ObservableArray<IEnvironmentRunObject>();
    private readonly _dataProviderId: string = "ms.vss-environments-web.environments-run-view-data-provider";
}

Services.add("pipelines-environments-tab-provider-service", { serviceFactory: EnvironmentsTabProviderService });
